/**
 * `native` plays only with the browser's decoder, `auto` falls back to the software decoder when the
 * browser can't decode the video, and `software` always uses the software decoder.
 */
export type VideoDecoderPreference = "native" | "auto" | "software";

export interface PlayerOptions {
  /** Silent, and repeating without a gap. */
  loop: boolean;
  /** Called with the media time of each frame as it is shown. */
  onFrame?: (mediaTime: number) => void;
}

export interface PlaybackState {
  started: boolean;
  currentTime: number;
  duration: number;
  playing: boolean;
  waiting: boolean;
  seeking: boolean;
  hasFrame: boolean;
  ended: boolean;
  volume: number;
  muted: boolean;
  error: string | null;
}

export type ReportPlayback = (state: Partial<PlaybackState>) => void;

export interface VideoPlayback {
  getCurrentTime(): number;
  prepare(): void;
  play(): Promise<void>;
  pause(): void;
  seek(seconds: number): void;
  setVolume(volume: number): void;
  setMuted(muted: boolean): void;
  dispose(): void;
}

export function initialPlaybackState(duration: number): PlaybackState {
  return {
    started: false,
    currentTime: 0,
    duration,
    playing: false,
    waiting: false,
    seeking: false,
    hasFrame: false,
    ended: false,
    volume: 1,
    muted: false,
    error: null,
  };
}

export function playbackTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export function boundedTime(seconds: number, duration: number): number {
  return Math.max(0, Math.min(duration, seconds));
}
